import { find, groupBy, remove, toPath } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';

const block = 'errors-presenter';

export type ErrorItemText = React.ReactNode;

export interface ErrorItem {
    path: string;
    text: ErrorItemText;
}

interface ErrorsPresenterProps {
    errors: ErrorItem[];
    onClear?: (pathes: string[]) => void;
}

export interface ErrorsPresenterContextProps {
    errors: { [path: string]: ErrorItem[] };
    onErrorsViewMount: (path: string) => void;
    onErrorsViewUnmount: (path: string) => void;
    onClear?: (path: string) => void;
}

export const ErrorsPresenterContext = React.createContext<
    ErrorsPresenterContextProps
>({
    errors: {},
    onErrorsViewMount: () => ({}),
    onErrorsViewUnmount: () => ({})
});

export class ErrorsPresenter extends React.PureComponent<ErrorsPresenterProps> {
    private pathes: { orig: string; parsed: string }[];

    constructor(props: ErrorsPresenterProps) {
        super(props);

        this.pathes = [];
    }

    render() {
        const groupedErrors = this.groupErrorsByAvailablePathes(
            this.props.errors
        );

        return (
            <div className={Bem.cls(block)}>
                <ErrorsPresenterContext.Provider
                    {...this.props}
                    value={{
                        errors: groupedErrors,
                        onErrorsViewMount: this.onErrorsViewMount,
                        onErrorsViewUnmount: this.onErrorsViewUnmount,
                        onClear: this.props.onClear
                            ? this.onClearErrors
                            : undefined
                    }}
                />
            </div>
        );
    }

    private groupErrorsByAvailablePathes(errors: ErrorItem[]) {
        return groupBy(errors, error =>
            this.getMatchedPath(toPath(error.path))
        );
    }

    private onErrorsViewMount = (path: string) => {
        this.pathes.push({
            orig: path,
            parsed: toPath(path).join('.')
        });
    };

    private onErrorsViewUnmount = (path: string) => {
        this.pathes = remove(this.pathes, ({ orig }) => orig !== path);
    };

    private onClearErrors = (errorsViewPath: string) => {
        const { errors, onClear } = this.props;

        const clearedErrorPathes = errors
            .filter(({ path }) => {
                return this.getMatchedPath(toPath(path)) === errorsViewPath;
            })
            .map(({ path }) => path);

        if (onClear && clearedErrorPathes.length) {
            onClear(clearedErrorPathes);
        }
    };

    /**
     * Для переданного пути, возвращает наиболее подходящий путь из доступных в `this.pathes` или ''
     * @param {string[]} pathArray
     * @return {string}
     */
    private getMatchedPath(pathArray: string[]): string {
        const parsedPath = pathArray.join('.');
        const founded = find(this.pathes, { parsed: parsedPath });

        if (founded) {
            return founded.orig;
        } else if (pathArray.length) {
            return this.getMatchedPath(pathArray.slice(0, -1));
        }

        return '';
    }
}
